import React, {useState, useEffect} from 'react';
import {EdgeProps, Edge, useReactFlow} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

export const CustomStepEdge = (props: EdgeProps<Edge<Record<string, any>>>) => {
  const {
    id,
    sourceX,
    sourceY,
    targetX,
    targetY,
    data,
    sourcePosition,
    targetPosition,
    selected,
    style = {},
  } = props;

  const stroke = style.stroke || '#000';
  const strokeWidth = Number(style.strokeWidth) || 1;
  const interactionWidth = 20;

  const arrowColor = stroke;
  const arrowWidth = 6, // default arrow width
    arrowLength = 10; // default arrow length
  const controlPointRadius = 6; //Math.max(Number(strokeWidth) * 2, 5);

  const highlightColor = stroke;
  const glowColor = stroke;
  const glowWidth = selected ? strokeWidth * 1.5 : strokeWidth;

  const [control, setControl] = useState<{x: number; y: number}>({
    x: data?.controlX ? data.controlX : (sourceX + targetX) / 2,
    y: data?.controlY ? data.controlY : (sourceY + targetY) / 2,
  });
  // Store previous endpoints
  const prevEndpoints = React.useRef({sourceX, sourceY, targetX, targetY});

  // Update control point when edge endpoints change, keeping user-induced offset within bounds
  useEffect(() => {
    const prev = prevEndpoints.current;
    const oldMid = {x: (prev.sourceX + prev.targetX) / 2, y: (prev.sourceY + prev.targetY) / 2};
    const offset = {x: control.x - oldMid.x, y: control.y - oldMid.y};
    const newMid = {x: (sourceX + targetX) / 2, y: (sourceY + targetY) / 2};

    const margin = 20;
    const minX = Math.min(sourceX, targetX) + margin;
    const maxX = Math.max(sourceX, targetX) - margin;
    const minY = Math.min(sourceY, targetY) + margin;
    const maxY = Math.max(sourceY, targetY) - margin;

    const isSourceVertical = sourcePosition?.includes('top') || sourcePosition?.includes('bottom');
    const isTargetVertical = targetPosition?.includes('top') || targetPosition?.includes('bottom');

    // Only apply relevant offset based on connection types, with bounds checking
    if (isSourceVertical && isTargetVertical) {
      // For vertical connections, only keep vertical offset within bounds
      const newY = Math.max(minY, Math.min(newMid.y + offset.y, maxY));
      setControl({x: newMid.x, y: newY});
    } else if (!isSourceVertical && !isTargetVertical) {
      // For horizontal connections, only keep horizontal offset within bounds
      const newX = Math.max(minX, Math.min(newMid.x + offset.x, maxX));
      setControl({x: newX, y: newMid.y});
    } else {
      // For mixed connections, keep both offsets within bounds
      const newX = Math.max(minX, Math.min(newMid.x + offset.x, maxX));
      const newY = Math.max(minY, Math.min(newMid.y + offset.y, maxY));
      setControl({x: newX, y: newY});
    }

    prevEndpoints.current = {sourceX, sourceY, targetX, targetY};
  }, [sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition]);

  // Store control point location in data when control changes
  const {updateEdgeData} = useReactFlow();
  useEffect(() => {
    updateEdgeData(id, {controlX: control.x, controlY: control.y});
  }, [control]);

  const getStepPath = ({
    sourceX,
    sourceY,
    targetX,
    targetY,
    centerX,
    centerY,
    sourcePosition,
    targetPosition,
  }: {
    sourceX: number;
    sourceY: number;
    targetX: number;
    targetY: number;
    centerX: number;
    centerY: number;
    sourcePosition: string;
    targetPosition: string;
  }) => {
    const isSourceVertical = sourcePosition?.includes('top') || sourcePosition?.includes('bottom');
    const isTargetVertical = targetPosition?.includes('top') || targetPosition?.includes('bottom');

    if (isSourceVertical && isTargetVertical) {
      // Both nodes connected on vertical sides
      return `M${sourceX},${sourceY}
                        L${sourceX},${centerY}
                        L${targetX},${centerY}
                        L${targetX},${targetY}`;
    } else if (!isSourceVertical && !isTargetVertical) {
      // Both nodes connected on horizontal sides
      return `M${sourceX},${sourceY}
                        L${centerX},${sourceY}
                        L${centerX},${targetY}
                        L${targetX},${targetY}`;
    } else {
      // Mixed connection - decide based on overall path direction
      const verticalFirst = Math.abs(targetX - sourceX) < Math.abs(targetY - sourceY);
      return verticalFirst
        ? `M${sourceX},${sourceY}
                 L${sourceX},${centerY}
                 L${targetX},${centerY}
                 L${targetX},${targetY}`
        : `M${sourceX},${sourceY}
                 L${centerX},${sourceY}
                 L${centerX},${targetY}
                 L${targetX},${targetY}`;
    }
  };

  // Updated onDrag to allow horizontal movement if the underlying path is vertical,
  // and vertical movement if the underlying path is horizontal.
  const onDrag = (event: PointerEvent) => {
    const svg = (event.target as SVGElement).ownerSVGElement;
    if (svg && svg.getScreenCTM) {
        const point = svg.createSVGPoint();
        point.x = event.clientX;
        point.y = event.clientY;
        const transformedPoint = point.matrixTransform(svg.getScreenCTM()?.inverse());

        // Giới hạn trong phạm vi từ source đến target
        const minX = Math.min(sourceX, targetX);
        const maxX = Math.max(sourceX, targetX);
        const minY = Math.min(sourceY, targetY);
        const maxY = Math.max(sourceY, targetY);

        // Cập nhật vị trí, đảm bảo không vượt quá giới hạn
        const newX = Math.max(minX, Math.min(transformedPoint.x, maxX));
        const newY = Math.max(minY, Math.min(transformedPoint.y, maxY));

        setControl({ x: newX, y: newY });
    }
  };

  const edgePath = getStepPath({
    sourceX,
    sourceY,
    targetX,
    targetY,
    centerX: control.x,
    centerY: control.y,
    sourcePosition,
    targetPosition,
  });

  return (
    <>
      <defs>
        {/* <style>{`
          @keyframes dash {
            to { stroke-dashoffset: -10; }
          }
        `}</style> */}
        <filter id={`glow-${id}`}>
          <feGaussianBlur stdDeviation="2" result="coloredBlur" />
          <feFlood floodColor={glowColor} floodOpacity="0.4" />
          <feComposite in2="coloredBlur" operator="in" />
          <feMerge>
            <feMergeNode />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        <marker
          id={`arrow-${id}`}
          viewBox={`0 0 10 10`}
          refX={10}
          refY={5}
          markerWidth={arrowWidth}
          markerHeight={arrowLength}
          strokeWidth={1}
          orient="auto-start-reverse">
          <path d={`M 0 0 L 10 5 L 0 10 z`} strokeWidth={1} fill={arrowColor} />
        </marker>
      </defs>
      {interactionWidth && (
        <path
          d={edgePath}
          fill="none"
          strokeOpacity={0}
          strokeWidth={interactionWidth}
          className="react-flow__edge-interaction"
        />
      )}
      {/* normal path */}
      <path
        id={id}
        d={edgePath}
        fill="none"
        stroke={stroke}
        strokeWidth={strokeWidth}
        strokeDasharray={style.strokeDasharray}
        // filter={selected ? `url(#glow-${id})` : undefined}
      />
      {/* path thường để mũi tên nhất quán, không bị to quá */}
      <path
        id={id + 'B'}
        d={edgePath}
        fill="none"
        stroke={stroke}
        strokeWidth={Math.min(strokeWidth, 2)}
        strokeDasharray={style.strokeDasharray}
        filter={selected ? `url(#glow-${id})` : undefined}
        markerEnd={`url(#arrow-${id})`}
      />
      <circle
        cx={control.x}
        cy={control.y}
        r={selected ? controlPointRadius + 2 : controlPointRadius}
        fill={selected ? highlightColor : '#fff'}
        stroke={glowColor}
        strokeWidth={selected ? 2 : 1}
        cursor="move"
        pointerEvents="all"
        onPointerDown={event => {
          event.preventDefault();
          const target = event.target as SVGCircleElement;
          target.setPointerCapture(event.pointerId);
          target.onpointermove = onDrag as any;
          target.onpointerup = () => (target.onpointermove = null);
        }}
      />
    </>
  );
};
